<script setup lang="ts">
/**
 * SignupSegmentFields — 가입 폼 선택②(맞춤 정보) 입력 블록.
 *
 * 로컬·구글 두 가입 경로가 **같은 블록을 공유**한다. 두 벌로 두면 SSO 쪽만 항목이 빠지거나
 * 유도 문구가 어긋나는 드리프트가 생긴다(설계 §4.6 — 두 경로는 Step 2 를 공유한다).
 *
 * 🔴 **법적 계약 3가지 — 수정 시 반드시 지킬 것**(설계 10번 §4.3):
 *   1. 전 항목 선택 입력이다. 필수 표시(*)·필수 검증을 넣지 않는다 — 개보법 §22⑤ 위반.
 *   2. "건너뛰기" 버튼을 두지 않는다. 강제가 아니라 동선 설계다(스킵 링크가 없으면 대부분 채운다).
 *   3. 하단 안전판 문구(입력 안 해도 제한 없음)는 **단정문으로 유지**한다. "제한은 없지만
 *      입력하면 더 좋다" 식으로 바꾸면 단정이 양보절로 격하돼 §22⑤ 준수 증명이 사라지고,
 *      무게중심이 "안 쓰면 손해"로 옮겨가 사실상 강요로 읽힌다. 혜택 서술은 상단 유도 문구 담당.
 *
 * <b>동의 체크박스는 여기 없다</b> — 가입 폼 하단 동의 그룹(전체 동의 + 5종)으로 옮겼다.
 * 입력 칸 사이에 동의가 끼어 있으면 (a) 필수 동의들과 흩어져 스캔이 어렵고, (b) 아직 아무것도
 * 고르지 않았는데 "입력한 정보를 활용"에 동의하는 순서 역전이 생긴다. 이 블록은 입력만 소유한다.
 *
 * 유도 문구는 프리필 이익만 서술한다 — 마케팅 소관(설명회·이벤트 안내)이나 미구현 기능
 * (지역·학년 맞춤 표시)을 근거로 삼지 않는다(설계 §4.3 금지표).
 */
import { computed } from 'vue';

import { BaseField, BaseSelect } from '#/components/ui';
import HighschoolAutocomplete from '#/components/fo/HighschoolAutocomplete.vue';
import {
  MEMBER_ROLE_OPTIONS,
  REGION_OPTIONS,
  STUDENT_GRADE_OPTIONS,
  gradeFieldLabel,
  schoolFieldLabel,
  showsGradeField,
  showsSchoolField,
} from '#/data/signupSegmentOptions';

/**
 * 세그먼트 6필드를 <b>필드별 `defineModel`</b> 로 받는다.
 *
 * 객체 하나를 통째로 넘기지 않는 이유: 부모의 `reactive` 객체를 prop 으로 받아 필드를 고치면
 * 실제로는 동작하지만 `update:modelValue` 가 발생하지 않아 계약이 거짓이 되고, Vue 의
 * `no-mutating-props` 규약에도 정면으로 어긋난다. 스칼라 6개는 각자 진짜 양방향 바인딩이 된다.
 */
const memberRole = defineModel<string>('memberRole', { required: true });
const memberRoleEtc = defineModel<string>('memberRoleEtc', { required: true });
const studentGrade = defineModel<string>('studentGrade', { required: true });
const region = defineModel<string>('region', { required: true });
const schoolId = defineModel<number | null>('schoolId', { required: true });
const schoolEtc = defineModel<string>('schoolEtc', { required: true });

/** 학년은 학생·학부모만(본인/자녀 학년). 교사에게는 성립하지 않는다. */
const showsGrade = computed(() => showsGradeField(memberRole.value));
/** 학교는 재학(학생·학부모) + 재직(교사·진학상담교사). 학원 강사는 마스터에 없어 제외. */
const showsSchool = computed(() => showsSchoolField(memberRole.value));
/** 학부모의 학년은 "자녀 학년"이다 — 주체가 달라 라벨을 바꾼다. */
const gradeLabel = computed(() => gradeFieldLabel(memberRole.value));
/** 교사·진학상담교사의 학교는 "재직 학교"다. */
const schoolLabel = computed(() => schoolFieldLabel(memberRole.value));
/** 역할이 기타일 때만 자유입력을 노출한다. */
const showsRoleEtc = computed(() => memberRole.value === 'ETC');
</script>

<template>
  <section class="mb-5 mt-1 rounded-card border border-line-2 bg-bg-3 p-4">
    <h2 class="m-0 text-[13.5px] font-bold text-brand">맞춤 정보 (선택 입력)</h2>
    <p class="mx-0 mb-3.5 mt-1.5 text-[12.5px] leading-[1.6] text-body">
      학교·학년을 입력하시면 입시 분석에서 다시 입력하지 않아도 됩니다.
    </p>

    <!--
      역할·지역은 짧아 한 줄에 나란히 둔다 — 선택 정보가 필수 정보(아이디·비번·이메일)보다
      시각적으로 무거워지면 우선순위가 역전된다.

      ⚠️ 브레이크포인트는 `md`(600px)다. 이 프로젝트의 `sm` 은 380px 이라 가입 카드(520px) 안에서는
      사실상 항상 참이 돼, 모바일에서도 2열로 쪼개져 칸이 좁아지고 placeholder·값이 잘린다.
    -->
    <div class="grid gap-x-3 md:grid-cols-2">
      <BaseSelect
        v-model="memberRole"
        label="어떤 분이신가요?"
        placeholder="선택"
        clearable
        :options="MEMBER_ROLE_OPTIONS"
      />
      <BaseSelect
        v-model="region"
        label="지역"
        placeholder="선택"
        clearable
        :options="REGION_OPTIONS"
      />
    </div>

    <BaseField
      v-if="showsRoleEtc"
      v-model="memberRoleEtc"
      label="직접 입력"
      placeholder="예: 진로교사"
    />

    <!--
      학년·학교는 노출 조건이 다르다 — 교사·진학상담교사는 재직 학교만 받고 학년은 받지 않는다.
      한쪽만 뜨는 경우 남는 칸이 비지 않게 각각 독립 배치로 둔다.
    -->
    <div v-if="showsGrade" class="grid gap-x-3 md:grid-cols-2">
      <BaseSelect
        v-model="studentGrade"
        :label="gradeLabel"
        placeholder="선택"
        clearable
        :options="STUDENT_GRADE_OPTIONS"
      />
      <HighschoolAutocomplete
        v-if="showsSchool"
        v-model:school-id="schoolId"
        v-model:school-etc="schoolEtc"
        :label="schoolLabel"
      />
    </div>
    <HighschoolAutocomplete
      v-else-if="showsSchool"
      v-model:school-id="schoolId"
      v-model:school-etc="schoolEtc"
      :label="schoolLabel"
      hint="재직 중인 학교를 입력하지 않으셔도 됩니다."
    />

    <!--
      법적 안전판 — 제22조 제5항 준수를 화면에서 증명한다. 삭제·완화 금지(설계 §4.3).
      두 문장으로 끊어 "안 써도 괜찮다"를 먼저 전하되, 제한 없음은 단정으로 남긴다.
    -->
    <p class="mx-0 mb-0 mt-1 text-[11.5px] leading-[1.6] text-muted">
      입력하지 않으셔도 됩니다. 회원가입과 모든 자료 이용에 제한이 없습니다.
    </p>
  </section>
</template>
